import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { BaseComponent } from 'src/app/core/abstracts/baseComponent';
import { ConferenceEligibilityService } from 'src/app/core/services/conference-eligibility.service';
import {
  ConferenceApplicationModel,
  ConferenceEligibilityEmployeeViewModel,
  ConferenceEligibilityLevelResultModel
} from 'src/app/models/conferenceEligibilityModels';
import { AlertService } from 'src/app/shared/services/alert.service';

@Component({
  selector: 'app-conference-eligibility-widget',
  templateUrl: './conference-eligibility-widget.component.html',
  standalone: false
})
export class ConferenceEligibilityWidgetComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() userId?: number;
  @Input() selfView = true;

  view: ConferenceEligibilityEmployeeViewModel;
  selectedApplication: ConferenceApplicationModel;
  activeTab: 'eligibility' | 'applications' = 'eligibility';
  loading = false;
  showHelp = false;
  showApplication = false;
  showPastApplications = false;

  constructor(
    private conferenceEligibilityService: ConferenceEligibilityService,
    private alertService: AlertService
  ) {
    super();
  }

  ngOnInit() {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.userId && !changes.userId.firstChange) {
      this.load();
    }
  }

  load() {
    this.loading = true;
    this.subs.push(
      this.conferenceEligibilityService.getEmployeeView(this.userId).subscribe({
        next: view => {
          this.view = view;
          this.loading = false;
        },
        error: error => {
          this.alertService.error(this.errorMessage(error, 'Unable to load conference eligibility.'));
          this.loading = false;
        }
      })
    );
  }

  getLevelStatus(result: ConferenceEligibilityLevelResultModel) {
    if (!result.roleAllowsLevel) {
      return 'Not available for this role';
    }
    if (result.eligible) {
      return 'Eligible';
    }
    if (!result.hasRequiredHours) {
      const remaining = Math.max(0, result.requiredHours - this.view.eligibility.availableHours);
      return `Not yet eligible - ${remaining.toFixed(2)} hours remaining`;
    }
    return 'Needs review';
  }

  viewApplication(application: ConferenceApplicationModel) {
    this.subs.push(
      this.conferenceEligibilityService.getApplication(application.id).subscribe({
        next: detail => {
          this.selectedApplication = detail;
          this.showApplication = true;
        },
        error: error => this.alertService.error(this.errorMessage(error, 'Unable to load application details.'))
      })
    );
  }

  cancelApplication() {
    if (!this.selectedApplication || !confirm('Cancel this conference application?')) {
      return;
    }

    this.subs.push(
      this.conferenceEligibilityService.cancelApplication(this.selectedApplication.id).subscribe({
        next: application => {
          this.selectedApplication = application;
          const index = this.view.applications.findIndex(item => item.id === application.id);
          if (index >= 0) {
            this.view.applications[index] = application;
          }
          this.alertService.success('Conference application cancelled.');
        },
        error: error => this.alertService.error(this.errorMessage(error, 'Unable to cancel the application.'))
      })
    );
  }

  canCancel(application: ConferenceApplicationModel) {
    return this.selfView && [7, 8, 9].includes(application?.statusID);
  }

  get visibleApplications() {
    const applications = this.view?.applications || [];
    return this.showPastApplications
      ? applications
      : applications.filter(application => !this.isPastConference(application));
  }

  isPastConference(application: ConferenceApplicationModel, today = new Date()) {
    if (!application?.conferenceEndDate) {
      return false;
    }

    const conferenceEndDate = new Date(application.conferenceEndDate);
    if (isNaN(conferenceEndDate.getTime())) {
      return false;
    }

    conferenceEndDate.setHours(23, 59, 59, 999);
    return conferenceEndDate < today;
  }

  getActiveApplication(opportunityId: number) {
    return (this.view?.applications || []).find(application =>
      application.opportunityID === opportunityId
      && ![4, 5].includes(application.statusID));
  }

  selectTab(tab: 'eligibility' | 'applications') {
    this.activeTab = tab;
  }

  private errorMessage(error: any, fallback: string) {
    return typeof error?.error === 'string'
      ? error.error
      : error?.error?.message || error?.message || fallback;
  }
}
